import { ReactNode } from 'react';
import styled from 'styled-components';

import { Box } from 'components/Box';
import { AnchorButton } from 'components/Button/AnchorButton';
import { Image } from 'components/Image/Image';
import { Body } from 'components/Text/Body';
import { BodyLarge } from 'components/Text/BodyLarge';
import { H2 } from 'components/Text/H2';
import { HeadingSmall } from 'components/Text/HeadingSmall';
import { RichText } from 'components/Text/RichText';
import { AmpVideoPlayer } from 'components/VideoPlayer';
import {
  CmsApiSplitContentBlockData,
  CmsApiSplitContentSection,
} from 'modules/cms/api/types/blocks/CmsApiSplitContentBlock';
import { gutterWidth } from 'theme';
import { imageUrl } from 'utils/filestack';
import { getImageSrc } from 'utils/getImageSrc';

const GifImage = styled(Image)<{ layout?: string }>`
  max-width: 100%;
  width: auto;
  @supports (object-fit: contain) {
    object-fit: contain;
    width: 100%;
  }
`;
const ContentContainer = styled(Box)<{ verticalCenter?: boolean }>`
  ${({ verticalCenter }) =>
    verticalCenter &&
    `
    margin: auto 0;
  `}
`;

function Content({ section }: { section: CmsApiSplitContentSection }) {
  switch (section.type) {
    case 'TEXT': {
      return (
        <>
          {section.data.label && (
            <HeadingSmall>{section.data.label}</HeadingSmall>
          )}
          {section.data.headline && (
            <H2
              dangerouslySetInnerHTML={{
                __html: section.data.headline,
              }}
            />
          )}
          <RichText dangerouslySetInnerHTML={{ __html: section.data.html }} />
          {section.data.button && (
            // @ts-expect-error Bug https://www.pivotaltracker.com/story/show/185222835
            <AnchorButton href={section.data.button.data.url}>
              {section.data.button.text}
            </AnchorButton>
          )}
        </>
      );
    }

    case 'BLOCKQUOTE':
      return (
        <Box px={[0, null, gutterWidth]}>
          <RichText>
            <Box component="blockquote">
              <BodyLarge
                dangerouslySetInnerHTML={{
                  __html: section.data.quote,
                }}
              />
              {section.data.cite && (
                <Box mt="18px">
                  <Body>{section.data.cite}</Body>
                </Box>
              )}
            </Box>
          </RichText>
        </Box>
      );

    case 'VIDEO':
      return <AmpVideoPlayer url={section.data.url} />;

    case 'IMAGE': {
      const isGif = section.data.image.mimetype === 'image/gif';
      return (
        <Box height="100%">
          {isGif ? (
            <GifImage
              layout="fixed-height"
              height={300}
              src={imageUrl({
                handle: section.data.image.handle,
                mimetype: section.data.image.mimetype,
              })}
              alt=""
            />
          ) : (
            <Box
              height="100%"
              minHeight={300}
              borderRadius={!isGif && 24}
              backgroundImage={`url(${getImageSrc({
                handle: section.data.image.handle,
                mimetype: section.data.image.mimetype,
              })})`}
              backgroundSize="cover"
              backgroundPosition="center"
            />
          )}
        </Box>
      );
    }

    default:
      return null;
  }
}

type Props = {
  data: CmsApiSplitContentBlockData;
};

export function CmsAmpSplitContentBlock({ data }: Props) {
  return (
    <Box
      display={['block', null, 'flex']}
      mx={[0, null, -gutterWidth]}
      my={[-gutterWidth / 2, null, 0]}
      width={[1, null, `calc(100% + ${gutterWidth * 2}px)`]}
    >
      {data.sections.map<ReactNode>((section, index) => (
        <ContentContainer // eslint-disable-next-line react/no-array-index-key
          key={index}
          width={[1, null, 1 / 2]}
          px={[0, null, gutterWidth]}
          py={[gutterWidth / 2, null, 0]}
          verticalCenter={
            section.type === 'IMAGE' &&
            section.data.image.mimetype === 'image/gif'
          }
        >
          <Content section={section} />
        </ContentContainer>
      ))}
    </Box>
  );
}
